import { useEffect, useRef, type ReactNode, type PointerEvent } from 'react';
import { X } from 'lucide-react';
import './BottomSheet.css';

export interface BottomSheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}

const SWIPE_CLOSE_PX = 90;

/**
 * Full-height mobile sheet (W05 Member 360). Closes on Escape, backdrop tap, the close
 * button, or a downward swipe on the drag handle. Focus moves into the sheet on open and
 * returns to the previously focused element on close.
 */
export function BottomSheet({ open, title, onClose, children, testId }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const onPointerDown = (e: PointerEvent) => {
    dragStart.current = e.clientY;
  };
  const onPointerMove = (e: PointerEvent) => {
    if (dragStart.current === null || !sheetRef.current) return;
    const delta = Math.max(0, e.clientY - dragStart.current);
    sheetRef.current.style.transform = `translateY(${delta}px)`;
  };
  const onPointerUp = (e: PointerEvent) => {
    if (dragStart.current === null) return;
    const delta = e.clientY - dragStart.current;
    dragStart.current = null;
    if (sheetRef.current) sheetRef.current.style.transform = '';
    if (delta > SWIPE_CLOSE_PX) onClose();
  };

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={sheetRef}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId ?? 'bottom-sheet'}
      >
        <div
          className="sheet-handle-zone"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="sheet-handle" aria-hidden />
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}
